/**
 * Chutt... V2 - Radar Page (Mockup Iteration 2)
 * Header ivory "Pouls du Jour" + Mission pills filter + Split photo/KPIs + Collapsible sections
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  Button,
  Alert,
  Skeleton,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ShieldIcon from '@mui/icons-material/Shield';
import CampaignIcon from '@mui/icons-material/Campaign';
import HandshakeIcon from '@mui/icons-material/Handshake';
import GroupsIcon from '@mui/icons-material/Groups';
import { AnimatePresence, motion } from 'framer-motion';

import WeatherWidget from '../components/radar/WeatherWidget';
import TrendPredictionCard from '../components/radar/TrendPredictionCard';
import MentionCard from '../components/mentions/MentionCard';
import TrendingKeywordsCloud from '../components/charts/TrendingKeywordsCloud';
import ShareOfVoiceChart from '../components/charts/ShareOfVoiceChart';
import SegmentWheel from '../components/charts/SegmentWheel';
import GlassCard from '../components/shared/GlassCard';
import ExpandableKPI from '../components/shared/ExpandableKPI';
import ScrollReveal from '../components/shared/ScrollReveal';
import LastScanLabel from '../components/shared/LastScanLabel';
import { radarAPI } from '../services/apiV2';
import { analyticsAPI, reportsAPI, monitoringAPI, seoAPI, missionsAPI, objectivesAPI } from '../services/api';
import { invalidateBrandCache } from '../services/cache';

import { colors } from '../styles/colors';
import { stripLeadingBrandName } from '../utils/textHelpers';

/** Strip markdown formatting symbols # and * from text */
function cleanMarkdown(text) {
  if (!text) return text;
  return text
    .replace(/#{1,6}\s*/g, '')   // remove heading markers
    .replace(/\*{1,3}/g, '')     // remove bold/italic markers
    .replace(/\[\d+\]/g, '')     // remove reference numbers [1], [2], etc.
    .replace(/\s{2,}/g, ' ')     // collapse multiple spaces
    .trim();
}

const DATA_CACHE_TTL = 24 * 60 * 60 * 1000; // 24 heures

const MISSION_ICONS = {
  protect: <ShieldIcon sx={{ fontSize: 18 }} />,
  visibility: <CampaignIcon sx={{ fontSize: 18 }} />,
  trust: <HandshakeIcon sx={{ fontSize: 18 }} />,
  community: <GroupsIcon sx={{ fontSize: 18 }} />,
};


function RadarPage() {
  const { selectedBrand, brands, periodDays, setPeriodDays, hasCrisis, hasAlerts, cacheVersion, lastUpdated } = useOutletContext();
  const navigate = useNavigate();

  const [briefing, setBriefing] = useState(null);
  const [hasBriefing, setHasBriefing] = useState(false);
  const [trends, setTrends] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const loadedOnce = useRef(false);
  const [generating, setGenerating] = useState(false);
  const [generatingStep, setGeneratingStep] = useState('');
  const [error, setError] = useState(null);
  const [briefingExpanded, setBriefingExpanded] = useState(false);

  // V1 enrichment state
  const [brandSummary, setBrandSummary] = useState(null);
  const [todayKPIs, setTodayKPIs] = useState(null);
  const [monthlyTopics, setMonthlyTopics] = useState([]);
  const [serpData, setSerpData] = useState(null);
  const [mentions, setMentions] = useState([]);
  const [mentionsLoading, setMentionsLoading] = useState(false);
  const [scanningSerp, setScanningSerp] = useState(false);

  // Missions state
  const [missions, setMissions] = useState([]);
  const [, setMissionsLoading] = useState(false);
  const [selectedMission, setSelectedMission] = useState(null);

  // Brand image state
  const [brandImageUrl, setBrandImageUrl] = useState(null);

  // Objectives progress state
  const [objectivesProgress, setObjectivesProgress] = useState(null);

  // Report editor

  // KPI expansion state (accordion — only one at a time)
  const [expandedKPI, setExpandedKPI] = useState(null);

  // Mention filters (periodDays shared from MainLayout)
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionPlatform, setMentionPlatform] = useState('all');
  const [mentionSentiment, setMentionSentiment] = useState('all');
  const [mentionsLimit, setMentionsLimit] = useState(10);

  // ALL mentions for charts (no limit) — separate from paginated mentions
  const [allMentions, setAllMentions] = useState([]);

  const brandName = brands?.find(b => b.id === selectedBrand)?.name || '';
  const profileType = brands?.find(b => b.id === selectedBrand)?.profile_type || 'organisation';

  // Cache keys
  const radarCacheKey = `radar_data_${selectedBrand}`;
  const v1CacheKey = `radar_v1_${selectedBrand}`;

  const fetchData = useCallback(async (force = false) => {
    if (!selectedBrand) return;

    // Check localStorage cache first (unless forced)
    if (!force) {
      try {
        const cached = localStorage.getItem(radarCacheKey);
        if (cached) {
          const { briefing: cBriefing, trends: cTrends, alerts: cAlerts, timestamp } = JSON.parse(cached);
          if (Date.now() - timestamp < DATA_CACHE_TTL) {
            setHasBriefing(!!cBriefing);
            setBriefing(cBriefing || null);
            setTrends(cTrends || []);
            setAlerts(cAlerts || []);
            setLoading(false);
            loadedOnce.current = true;
            return;
          }
        }
      } catch (_) { /* ignore parse errors */ }
    }

    if (!loadedOnce.current) setLoading(true);
    setError(null);

    try {
      const [briefingRes, trendsRes, alertsRes] = await Promise.all([
        radarAPI.getBriefing(selectedBrand),
        radarAPI.getTrends(selectedBrand),
        radarAPI.getAlerts(selectedBrand),
      ]);

      const briefingData = briefingRes.data?.briefing || null;
      const trendsData = trendsRes.data?.trends || [];
      const alertsData = alertsRes.data?.alerts || [];

      setHasBriefing(!!briefingData);
      setBriefing(briefingData);
      setTrends(trendsData);
      setAlerts(alertsData);

      // Save to localStorage cache
      try {
        localStorage.setItem(radarCacheKey, JSON.stringify({
          briefing: briefingData, trends: trendsData, alerts: alertsData,
          timestamp: Date.now(),
        }));
      } catch (_) { /* localStorage full — ignore */ }
    } catch (err) {
      console.error('Radar fetch error:', err);
      setError('Erreur lors du chargement des donnees');
    } finally {
      setLoading(false);
      loadedOnce.current = true;
    }
  }, [selectedBrand, radarCacheKey]);

  // Fetch V1 enrichment data (with 24h localStorage cache)
  const fetchV1Data = useCallback(async (force = false) => {
    if (!selectedBrand) return;

    // Check localStorage cache first (unless forced)
    if (!force) {
      try {
        const cached = localStorage.getItem(v1CacheKey);
        if (cached) {
          const { summary, today, topics, serp, timestamp } = JSON.parse(cached);
          if (Date.now() - timestamp < DATA_CACHE_TTL) {
            setBrandSummary(summary);
            setTodayKPIs(today);
            setMonthlyTopics(topics || []);
            setSerpData(serp);
            return;
          }
        }
      } catch (_) { /* ignore parse errors */ }
    }

    try {
      // BrandSummary is written once at brand creation (wizard) — never re-fetched on Actualiser.
      // Only fetch it on initial mount (force=false).
      const fetches = [
        force ? Promise.resolve({ status: 'skipped' }) : reportsAPI.getDashboardSummary(selectedBrand),
        analyticsAPI.getToday(selectedBrand, force),
        analyticsAPI.getMonthlyTopics(selectedBrand, null, null, 10, force),
        seoAPI.getSerpResults(selectedBrand, null, force),
      ];
      const [summaryRes, todayRes, topicsRes, serpRes] = await Promise.allSettled(fetches);

      const summaryData = (!force && summaryRes.status === 'fulfilled') ? (summaryRes.value?.data?.summary || summaryRes.value?.data || null) : null;
      const todayData = todayRes.status === 'fulfilled' ? (todayRes.value?.data || null) : null;
      const topicsData = topicsRes.status === 'fulfilled' ? (topicsRes.value?.data?.topics || []) : [];
      const serpDataRes = serpRes.status === 'fulfilled' ? (serpRes.value?.data || null) : null;

      if (!force) setBrandSummary(summaryData);  // Only update summary on initial load
      setTodayKPIs(todayData);
      setMonthlyTopics(topicsData);
      setSerpData(serpDataRes);

      // Save to localStorage cache (preserve existing summary when force-refreshing)
      try {
        const cachedSummary = force ? JSON.parse(localStorage.getItem(v1CacheKey) || '{}').summary || null : summaryData;
        localStorage.setItem(v1CacheKey, JSON.stringify({
          summary: cachedSummary, today: todayData, topics: topicsData, serp: serpDataRes,
          timestamp: Date.now(),
        }));
      } catch (_) { /* localStorage full — ignore */ }
    } catch (err) {
      console.error('V1 enrichment fetch error:', err);
    }
  }, [selectedBrand, v1CacheKey]);

  // Fetch missions progress
  const fetchMissions = useCallback(async () => {
    if (!selectedBrand) return;
    setMissionsLoading(true);
    try {
      const res = await missionsAPI.getProgress(selectedBrand);
      const data = res.data || res;
      setMissions(data.missions || []);
    } catch (err) {
      console.warn('Missions fetch warning:', err);
    } finally {
      setMissionsLoading(false);
    }
  }, [selectedBrand]);

  // Fetch mentions
  const fetchMentions = useCallback(async () => {
    if (!selectedBrand) return;
    setMentionsLoading(true);
    try {
      const params = {
        brand_id: selectedBrand,
        days: periodDays,
        limit: mentionsLimit,
        offset: 0,
        sort_by: 'date',
      };
      if (mentionPlatform !== 'all') params.platform = mentionPlatform;
      if (mentionSentiment !== 'all') params.sentiment = mentionSentiment;
      if (mentionSearch.trim()) params.search = mentionSearch.trim();

      const res = await monitoringAPI.getMentions(params);
      setMentions(res.data?.mentions || []);
    } catch (err) {
      console.error('Mentions fetch error:', err);
    } finally {
      setMentionsLoading(false);
    }
  }, [selectedBrand, periodDays, mentionsLimit, mentionPlatform, mentionSentiment, mentionSearch]);

  // Fetch ALL mentions for charts (no limit, no filters except period)
  const fetchAllMentions = useCallback(async () => {
    if (!selectedBrand) return;
    try {
      const res = await monitoringAPI.getMentions({
        brand_id: selectedBrand,
        days: periodDays,
        limit: 500,
        offset: 0,
        sort_by: 'date',
      });
      setAllMentions(res.data?.mentions || []);
    } catch (err) {
      console.error('All mentions fetch error:', err);
    }
  }, [selectedBrand, periodDays]);

  // Fetch brand image (uploaded photo takes priority over scraped images)
  // Fetch brand image (use photo_urls[0] = slot 1 for Radar)
  const fetchBrandImage = useCallback(async (forceRefresh = false) => {
    if (!selectedBrand) return;
    try {
      const res = await monitoringAPI.getBrandImages(selectedBrand, forceRefresh);
      const data = res.data || {};
      const urls = data.photo_urls || [];
      const newUrl = urls[0] || data.brand_photo_url || null;
      if (newUrl) {
        // Preload image before swapping URL to avoid flicker
        const img = new Image();
        img.onload = () => setBrandImageUrl(newUrl);
        img.onerror = () => {}; // keep existing photo on error
        img.src = newUrl;
      }
    } catch (err) {
      // Silent fail — keep existing photo
    }
  }, [selectedBrand]);

  // Core data — single effect on brand change
  useEffect(() => {
    fetchData();
    fetchV1Data();
    fetchMissions();
    fetchBrandImage();
    // Fetch objectives progress
    if (selectedBrand) {
      objectivesAPI.getProgress(selectedBrand).then(res => {
        setObjectivesProgress(res.data || res);
      }).catch(() => setObjectivesProgress(null));
    }
  }, [selectedBrand]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch when CRON freshness invalidates caches (only on actual change, not on mount)
  const prevCacheVersion = useRef(cacheVersion);
  useEffect(() => {
    if (cacheVersion !== prevCacheVersion.current) {
      prevCacheVersion.current = cacheVersion;
      fetchData();
      fetchV1Data();
    }
  }, [cacheVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mentions — separate because depends on filters + periodDays
  useEffect(() => {
    fetchMentions();
    fetchAllMentions();
  }, [fetchMentions, fetchAllMentions]);

  const handleGenerateBriefing = async () => {
    if (!selectedBrand) return;
    setGenerating(true);
    setError(null);

    // Invalidate caches — force fresh data (Radar + Mesurer + Agir linked)
    try { localStorage.removeItem(radarCacheKey); } catch (_) {}
    try { localStorage.removeItem(v1CacheKey); } catch (_) {}
    try { localStorage.removeItem(`mesurer_data_${selectedBrand}`); } catch (_) {}
    try { localStorage.removeItem(`mesurer_dashboard_${selectedBrand}`); } catch (_) {}
    try { localStorage.removeItem(`agir_actions_${selectedBrand}`); } catch (_) {}
    // Also invalidate in-memory cache for this brand
    invalidateBrandCache(selectedBrand);

    try {
      // === ETAPE 1 : Collecte de toutes les sources ===
      setGeneratingStep('Collecte des mentions (RSS, News, reseaux sociaux)...');
      try {
        await monitoringAPI.collectAll(selectedBrand, {
          daysLookback: 1,
          maxPerSource: 30,
        });
      } catch (collectErr) {
        console.warn('Collect-all partial error (continuing):', collectErr);
      }

      // === ETAPE 1b : Mise a jour de la photo ===
      setGeneratingStep('Mise a jour de la photo...');
      try {
        await Promise.allSettled([
          monitoringAPI.fetchBrandImages(selectedBrand, true).then(() => fetchBrandImage(true)),
        ]);
      } catch (_) { /* non-blocking */ }

      // === ETAPE 2 : Analyses en parallele (briefing IA + tendances + SERP) ===
      setGeneratingStep('Analyse IA et scan SERP...');
      setScanningSerp(true);
      const [briefingRes, trendsRes, serpRes] = await Promise.allSettled([
        radarAPI.generateBriefing(selectedBrand, profileType),
        radarAPI.analyzeTrends(selectedBrand),
        seoAPI.scanSerp(selectedBrand, null, true),
      ]);

      const freshBriefing = briefingRes.status === 'fulfilled' ? (briefingRes.value?.data?.briefing || null) : null;
      const freshTrends = trendsRes.status === 'fulfilled' ? (trendsRes.value?.data?.trends || []) : [];
      const freshAlerts = [];

      if (freshBriefing) {
        setBriefing(freshBriefing);
        setHasBriefing(true);
      } else if (briefingRes.status === 'rejected') {
        console.error('Briefing error:', briefingRes.reason);
      }

      setTrends(freshTrends);

      if (serpRes.status === 'fulfilled') {
        setSerpData(serpRes.value?.data || null);
      } else if (serpRes.status === 'rejected') {
        console.error('SERP scan error:', serpRes.reason);
      }
      setScanningSerp(false);

      // === ETAPE 3 : Rafraichir tous les indicateurs ===
      setGeneratingStep('Mise a jour des indicateurs...');
      await Promise.allSettled([
        fetchV1Data(true),
        fetchMentions(),
      ]);

      // Save fresh radar data to localStorage cache
      try {
        localStorage.setItem(radarCacheKey, JSON.stringify({
          briefing: freshBriefing, trends: freshTrends, alerts: freshAlerts,
          timestamp: Date.now(),
        }));
      } catch (_) { /* localStorage full — ignore */ }

      setGeneratingStep('');

    } catch (err) {
      console.error('Actualiser error:', err);
      setError('Erreur lors de la mise a jour. Certaines donnees peuvent etre partielles.');
    } finally {
      setGenerating(false);
      setScanningSerp(false);
      setGeneratingStep('');
    }
  };

  // Extract keywords from monthly topics for cloud
  const cloudKeywords = useMemo(() => {
    if (!monthlyTopics || monthlyTopics.length === 0) return [];
    const kwMap = {};
    monthlyTopics.forEach(t => {
      const kws = t.keywords || [];
      kws.forEach(kw => {
        const lower = kw.toLowerCase();
        kwMap[lower] = (kwMap[lower] || 0) + (t.mention_count || t.mentions || 1);
      });
      if (t.name || t.topic_name) {
        const name = (t.name || t.topic_name).toLowerCase();
        kwMap[name] = (kwMap[name] || 0) + (t.mention_count || t.mentions || 1);
      }
    });
    return Object.entries(kwMap)
      .map(([text, count]) => ({ text, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 30);
  }, [monthlyTopics]);

  // SERP computed stats
  const serpStats = useMemo(() => {
    if (!serpData?.results || serpData.results.length === 0) return null;
    const results = serpData.results;
    const top10 = results.filter(r => r.position <= 10);
    const positive = top10.filter(r => r.sentiment === 'positive').length;
    const negative = top10.filter(r => r.sentiment === 'negative').length;
    const neutral = top10.length - positive - negative;
    const score = top10.length > 0 ? Math.round((positive / top10.length) * 100) : 0;
    return { score, positive, negative, neutral, total: top10.length };
  }, [serpData]);

  // localStorage-based trend tracking for TENDANCES and SERP
  const trendsCount = trends.length || cloudKeywords.length || 0;

  const trendsDelta = useMemo(() => {
    if (!selectedBrand || trendsCount === 0) return null;
    const key = `chutt_trends_prev_${selectedBrand}`;
    const prev = parseInt(localStorage.getItem(key), 10);
    localStorage.setItem(key, String(trendsCount));
    if (isNaN(prev) || prev === 0) return null;
    const diff = trendsCount - prev;
    return { diff, label: diff > 0 ? `+${diff} vs dernier scan` : diff < 0 ? `${diff} vs dernier scan` : 'Stable vs dernier scan' };
  }, [selectedBrand, trendsCount]);

  const serpDelta = useMemo(() => {
    if (!selectedBrand || !serpStats) return null;
    const key = `chutt_serp_prev_${selectedBrand}`;
    const prev = parseInt(localStorage.getItem(key), 10);
    localStorage.setItem(key, String(serpStats.score));
    if (isNaN(prev)) return null;
    const diff = serpStats.score - prev;
    return { diff, label: diff > 0 ? `+${diff}% vs dernier scan` : diff < 0 ? `${diff}% vs dernier scan` : 'Stable vs dernier scan' };
  }, [selectedBrand, serpStats]);

  // Platform breakdown for ShareOfVoice + SegmentWheel — uses ALL mentions (not paginated)
  const platformSources = useMemo(() => {
    const source = allMentions.length > 0 ? allMentions : mentions;
    if (!source || source.length === 0) {
      return [];
    }
    const counts = {};
    source.forEach(m => {
      const p = m.platform || 'web';
      counts[p] = (counts[p] || 0) + 1;
    });
    return Object.entries(counts).map(([platform, count]) => ({ platform, count }));
  }, [allMentions, mentions]);

  // Sentiment breakdown for SegmentWheel — uses ALL mentions (not paginated)
  const sentimentBreakdown = useMemo(() => {
    const source = allMentions.length > 0 ? allMentions : mentions;
    if (!source || source.length === 0) {
      const pos = todayKPIs?.today?.positive || 0;
      const neg = todayKPIs?.today?.negative || 0;
      const total = todayKPIs?.today?.total_mentions || 0;
      const neu = Math.max(0, total - pos - neg);
      if (total === 0) return [];
      return [
        { name: 'Positif', value: pos, color: colors.success },
        { name: 'Neutre', value: neu, color: colors.textLight },
        { name: 'Negatif', value: neg, color: colors.danger },
      ];
    }
    const pos = source.filter(m => m.sentiment === 'positive').length;
    const neg = source.filter(m => m.sentiment === 'negative').length;
    const neu = source.length - pos - neg;
    return [
      { name: 'Positif', value: pos, color: colors.success },
      { name: 'Neutre', value: neu, color: colors.textLight },
      { name: 'Negatif', value: neg, color: colors.danger },
    ];
  }, [allMentions, mentions, todayKPIs]);

  const totalMentions = useMemo(() => {
    return allMentions.length || mentions.length || todayKPIs?.today?.total_mentions || 0;
  }, [allMentions, mentions, todayKPIs]);

  // Check if we have any data sections to show
  const hasKeywords = cloudKeywords.length > 0;
  const hasSerp = serpStats !== null;
  const hasTrends = trends.length > 0;
  const hasSources = platformSources.length > 0;

  if (!selectedBrand) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="textSecondary">
          Selectionnez une marque pour voir le Radar
        </Typography>
      </Box>
    );
  }

  const km = briefing?.key_metrics || {};
  // Truncate briefing text for compact display (strip markdown # and *)
  const briefingText = cleanMarkdown(briefing?.ai_summary) || 'Aucun briefing disponible.';
  const briefingTruncated = briefingText.length > 250;
  const briefingShort = briefingTruncated ? briefingText.substring(0, 250) + '...' : briefingText;

  // Truncate alert text for compact display
  const alertSummary = alerts.length > 0
    ? `${alerts.length} alerte${alerts.length > 1 ? 's' : ''} active${alerts.length > 1 ? 's' : ''} : ${alerts[0]?.message || alerts[0]?.title || 'Crise detectee'}`
    : '';

  const nssValue = km.nss ?? todayKPIs?.today?.nss ?? null;
  const reputationScore = Math.round(briefing?.reputation_score || 50);
  const dateFormatee = new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });


  return (
    <Box sx={{ pb: 4 }}>
      {/* Compact Alert Strip */}
      <AnimatePresence>
        {alerts.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 0.5,
              mb: 1.5,
              borderRadius: 1.5,
              bgcolor: alerts[0]?.severity >= 3 ? 'rgba(239,68,68,0.08)' : 'rgba(245,158,11,0.08)',
              border: `1px solid ${alerts[0]?.severity >= 3 ? 'rgba(239,68,68,0.2)' : 'rgba(245,158,11,0.2)'}`,
            }}
          >
            <Box
              sx={{
                width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                bgcolor: alerts[0]?.severity >= 3 ? colors.danger : colors.warning,
              }}
            />
            <Typography
              variant="body2"
              noWrap
              sx={{
                flex: 1, minWidth: 0, fontSize: '0.78rem', fontWeight: 500,
                fontFamily: '"DM Sans", sans-serif',
                color: alerts[0]?.severity >= 3 ? colors.priorityUrgent : colors.priorityHigh,
              }}
            >
              {alertSummary}
            </Typography>
            <Chip
              label="Gerer la crise"
              size="small"
              clickable
              onClick={() => navigate('/crisis')}
              sx={{ height: 20, fontSize: '0.6rem', fontWeight: 600, bgcolor: 'rgba(0,0,0,0.06)', flexShrink: 0 }}
            />
            <IconButton size="small" aria-label="Fermer l'alerte" onClick={() => setAlerts([])} sx={{ p: 0.3, flexShrink: 0 }}>
              <CloseIcon sx={{ fontSize: 14, color: colors.textLight }} />
            </IconButton>
          </Box>
        )}
      </AnimatePresence>

      {/* ===== HEADER IVORY MOCKUP ===== */}
      <ScrollReveal>
        <Box sx={{ pt: 5, pb: 4, mb: 4, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography
              sx={{
                fontFamily: '"Playfair Display", Georgia, serif',
                fontWeight: 400,
                fontSize: { xs: '3.2rem', sm: '4.8rem' },
                color: colors.charcoal,
                lineHeight: 1.1,
                mb: 1,
              }}
            >
              Pouls du{' '}
              <Box component="em" sx={{ color: colors.primary, fontStyle: 'italic' }}>
                Jour
              </Box>
            </Typography>
            <Typography
              sx={{
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '1rem',
                color: colors.textMed,
                fontWeight: 400,
              }}
            >
              {brandName} — {dateFormatee}. Votre reputation en un coup d'oeil.
            </Typography>
            <LastScanLabel timestamp={lastUpdated} />
          </Box>
          <Button
            variant="outlined"
            startIcon={generating ? <CircularProgress size={14} sx={{ color: colors.primary }} /> : <RefreshIcon />}
            onClick={handleGenerateBriefing}
            disabled={generating}
            sx={{
              borderColor: 'rgba(27,94,64,0.25)',
              color: colors.primary,
              borderRadius: '100px',
              textTransform: 'none',
              fontFamily: '"DM Sans", sans-serif',
              fontWeight: 600,
              flexShrink: 0,
              '&:hover': { borderColor: colors.primary, bgcolor: colors.primaryBg },
            }}
          >
            {generating ? 'Actualisation...' : hasBriefing ? 'Actualiser' : 'Generer le briefing'}
          </Button>
        </Box>
      </ScrollReveal>


      {/* Progress indicator during Actualiser */}
      {generating && generatingStep && (
        <Alert severity="info" icon={false} sx={{ mb: 2, py: 0.5 }}>
          <Typography variant="body2" sx={{ fontSize: '0.8rem', fontFamily: '"DM Sans", sans-serif' }}>
            {generatingStep}
          </Typography>
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading state */}
      {loading && (
        <Box sx={{ borderRadius: '16px', p: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={5}>
              <Skeleton variant="rounded" height={280} sx={{ borderRadius: '20px' }} />
            </Grid>
            <Grid item xs={12} sm={7}>
              <Grid container spacing={2}>
                <Grid item xs={6}><Skeleton variant="rounded" height={130} sx={{ borderRadius: '16px' }} /></Grid>
                <Grid item xs={6}><Skeleton variant="rounded" height={130} sx={{ borderRadius: '16px' }} /></Grid>
                <Grid item xs={6}><Skeleton variant="rounded" height={130} sx={{ borderRadius: '16px' }} /></Grid>
                <Grid item xs={6}><Skeleton variant="rounded" height={130} sx={{ borderRadius: '16px' }} /></Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Content */}
      {!loading && (
        <>
          {/* ===== ROW 1: HERO — Photo+Score LEFT | 4 Mission cards RIGHT ===== */}
          {/* Exact replica of mockup-FINAL.html .radar-hero layout */}
          <ScrollReveal delay={0.15}>
            <Box
              sx={{
                display: 'grid',
                /* 2 columns from 600px+, single column only on phones */
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: { xs: '16px', sm: '24px', lg: '48px' },
                alignItems: 'stretch',
                mb: 4,
              }}
            >
              {/* LEFT: Photo area with score overlay */}
              <Box
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: '24px',
                  /* Responsive height: scales with viewport, capped at 380px */
                  height: { xs: 280, sm: 320, lg: 380 },
                  background: brandImageUrl
                    ? `url(${brandImageUrl}) center 20%/cover no-repeat`
                    : `linear-gradient(135deg, ${colors.primary} 0%, ${colors.charcoal} 100%)`,
                }}
              >
                {/* Dark overlay gradient */}
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(180deg, transparent 40%, rgba(26,26,24,0.7) 100%)',
                    zIndex: 1,
                    borderRadius: '24px',
                  }}
                />

                {/* WeatherWidget — transparent photo overlay */}
                <WeatherWidget
                  score={reputationScore}
                  compact
                  forceCrisis={hasCrisis || hasAlerts}
                />

                {/* Score float — bottom left */}
                <Box sx={{ position: 'absolute', bottom: 24, left: 24, zIndex: 2 }}>
                  <Typography
                    component="span"
                    sx={{
                      fontFamily: '"Playfair Display", Georgia, serif',
                      fontWeight: 500,
                      fontSize: { xs: '4rem', sm: '5rem', lg: '6rem' },
                      color: '#FFFFFF',
                      lineHeight: 1,
                      fontVariantNumeric: 'tabular-nums',
                      textShadow: '0 2px 20px rgba(0,0,0,0.3)',
                    }}
                  >
                    {reputationScore}
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      fontFamily: '"Playfair Display", Georgia, serif',
                      fontSize: { xs: '1rem', lg: '1.5rem' },
                      color: 'rgba(255,255,255,0.5)',
                      position: 'relative',
                      top: { xs: -16, lg: -24 },
                      ml: 0.5,
                    }}
                  >
                    /100
                  </Typography>
                </Box>
              </Box>

              {/* RIGHT: 4 MISSION cards — 2x2 grid, same total height as photo */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gridTemplateRows: '1fr 1fr',
                  gap: '16px',
                  /* No explicit height — alignItems:'stretch' on parent makes it
                     match the photo height automatically */
                }}
              >
                {[
                  { key: 'protect', label: 'Proteger', icon: MISSION_ICONS.protect, context: 'de reputation preservee' },
                  { key: 'trust', label: 'Inspirer Confiance', icon: MISSION_ICONS.trust, context: 'de confiance etablie' },
                  { key: 'visibility', label: 'Rayonner', icon: MISSION_ICONS.visibility, context: 'de visibilite atteinte' },
                  { key: 'community', label: 'Federer', icon: MISSION_ICONS.community, context: 'de communaute engagee' },
                ].map((def, i) => {
                  const m = missions.find(x => x.mission_key === def.key);
                  const mScore = m ? Math.round(m.score || 0) : null;
                  const isActive = selectedMission === def.key;
                  return (
                    <motion.div
                      key={def.key}
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: i * 0.07 }}
                      style={{ display: 'flex', minHeight: 0 }}
                    >
                      <Box
                        onClick={() => m && setSelectedMission(isActive ? null : def.key)}
                        sx={{
                          flex: 1,
                          borderRadius: '20px',
                          border: `1.5px solid ${isActive ? colors.primary : colors.borderLight}`,
                          bgcolor: isActive ? 'rgba(27,94,64,0.06)' : colors.cardGlass,
                          p: { xs: '16px', sm: '18px 20px', lg: '26px 28px' },
                          cursor: m ? 'pointer' : 'default',
                          transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          overflow: 'hidden',
                          '&:hover': m ? {
                            borderColor: colors.primary,
                            boxShadow: '0 4px 20px rgba(27,94,64,0.08)',
                            transform: 'translateY(-2px)',
                          } : {},
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          {def.icon && React.cloneElement(def.icon, {
                            sx: { fontSize: 16, color: isActive ? colors.primary : colors.textMed },
                          })}
                          <Typography
                            sx={{
                              fontSize: { xs: '0.55rem', lg: '0.62rem' },
                              textTransform: 'uppercase',
                              letterSpacing: '0.1em',
                              fontWeight: 600,
                              fontFamily: '"DM Sans", sans-serif',
                              color: isActive ? colors.primary : colors.textLight,
                            }}
                          >
                            {def.label}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography
                            sx={{
                              fontFamily: '"Playfair Display", Georgia, serif',
                              fontSize: { xs: '1.8rem', sm: '2rem', lg: '2.6rem' },
                              fontWeight: 500,
                              color: mScore != null
                                ? (mScore >= 70 ? colors.success : mScore >= 40 ? colors.warning : colors.danger)
                                : colors.charcoal,
                              lineHeight: 1.1,
                              fontVariantNumeric: 'tabular-nums',
                            }}
                          >
                            {mScore != null ? mScore : '--'}
                            <Typography
                              component="span"
                              sx={{
                                fontSize: { xs: '0.7rem', lg: '0.9rem' },
                                color: colors.textMed,
                                fontFamily: '"DM Sans", sans-serif',
                                ml: 0.3,
                              }}
                            >
                              {mScore != null ? '%' : ''}
                            </Typography>
                          </Typography>
                          {mScore != null && (
                            <Typography
                              sx={{
                                fontSize: { xs: '0.62rem', lg: '0.72rem' },
                                fontFamily: '"DM Sans", sans-serif',
                                fontWeight: 400,
                                color: colors.textMed,
                                mt: 0.3,
                              }}
                            >
                              {def.context}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </motion.div>
                  );
                })}
              </Box>
            </Box>
          </ScrollReveal>

          {/* ===== ROW 2: BRIEFING — full width ===== */}
          <ScrollReveal delay={0.2}>
            <GlassCard
              hover
              glow={false}
              delay={0.05}
              onClick={() => setBriefingExpanded(!briefingExpanded)}
              sx={{ cursor: 'pointer', mb: 3 }}
            >
              <Box sx={{ p: '26px 28px' }}>
                <Typography
                  sx={{
                    fontSize: '0.62rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    color: colors.textMed,
                    fontFamily: '"DM Sans", sans-serif',
                    fontWeight: 600,
                    mb: '14px',
                  }}
                >
                  POULS DU JOUR
                </Typography>

                {/* BrandSummary — always visible at top */}
                {(brandSummary?.entity_description || brandSummary?.one_liner) && (
                  <Typography
                    sx={{
                      fontFamily: '"DM Sans", sans-serif',
                      fontSize: '0.82rem',
                      color: colors.charcoal,
                      lineHeight: 1.6,
                      mb: 1.5,
                      fontWeight: 500,
                      textAlign: 'justify',
                    }}
                  >
                    {stripLeadingBrandName(cleanMarkdown(brandSummary.entity_description || brandSummary.one_liner), brandName)}
                  </Typography>
                )}

                {/* Briefing reputation — below summary */}
                <Typography
                  sx={{
                    fontFamily: '"DM Sans", sans-serif',
                    fontSize: '0.82rem',
                    color: colors.textMed,
                    lineHeight: 1.7,
                    textAlign: 'justify',
                  }}
                >
                  {hasBriefing ? briefingShort : 'Cliquez sur "Generer le briefing" pour obtenir votre analyse quotidienne.'}
                </Typography>

                {/* Objectives progress summary */}
                {objectivesProgress && (() => {
                  const objectives = objectivesProgress.objectives || objectivesProgress || [];
                  if (!Array.isArray(objectives) || objectives.length === 0) return null;
                  const active = objectives.filter(o => o.status === 'active');
                  const behind = active.filter(o => (o.progress_pct || 0) < 50);
                  const achieved = objectives.filter(o => o.status === 'achieved');
                  if (active.length === 0 && achieved.length === 0) return null;
                  return (
                    <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5, flexWrap: 'wrap' }}>
                      {active.length > 0 && (
                        <Chip
                          label={`${active.length} objectif${active.length > 1 ? 's' : ''} en cours`}
                          size="small"
                          sx={{ height: 22, fontSize: '0.7rem', fontFamily: '"DM Sans", sans-serif', bgcolor: 'rgba(46,158,107,0.1)', color: colors.primary }}
                        />
                      )}
                      {behind.length > 0 && (
                        <Chip
                          label={`${behind.length} en retard`}
                          size="small"
                          sx={{ height: 22, fontSize: '0.7rem', fontFamily: '"DM Sans", sans-serif', bgcolor: 'rgba(185,28,28,0.1)', color: '#B91C1C' }}
                        />
                      )}
                      {achieved.length > 0 && (
                        <Chip
                          label={`${achieved.length} atteint${achieved.length > 1 ? 's' : ''}`}
                          size="small"
                          sx={{ height: 22, fontSize: '0.7rem', fontFamily: '"DM Sans", sans-serif', bgcolor: 'rgba(46,158,107,0.15)', color: colors.primary, fontWeight: 600 }}
                        />
                      )}
                    </Box>
                  );
                })()}

                {/* Expanded: full briefing details */}
                <AnimatePresence initial={false}>
                  {briefingExpanded && hasBriefing && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
                      style={{ overflow: 'hidden' }}
                    >
                      <Box
                        sx={{ mt: 2, pt: 2, borderTop: `1px solid ${colors.borderLight}` }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Typography
                          sx={{
                            color: colors.charcoal,
                            lineHeight: 1.7,
                            fontSize: '0.85rem',
                            fontFamily: '"DM Sans", sans-serif',
                            textAlign: 'justify',
                          }}
                        >
                          {briefingText}
                        </Typography>
                      </Box>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Box>
            </GlassCard>
          </ScrollReveal>

          {/* ===== ROW 3: PRIORITES ===== */}
          {briefing?.recommended_actions?.length > 0 && (
            <ScrollReveal delay={0.25}>
              <GlassCard hover={false} glow={false} sx={{ mb: 3 }}>
                <Box sx={{ p: '22px 28px' }}>
                  <Typography
                    sx={{
                      fontSize: '0.62rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.1em',
                      color: colors.textMed,
                      fontFamily: '"DM Sans", sans-serif',
                      fontWeight: 600,
                      mb: 1.5,
                    }}
                  >
                    PRIORITES
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {briefing.recommended_actions.slice(0, 4).map((a, idx) => (
                      <Chip
                        key={idx}
                        label={a.action}
                        size="small"
                        sx={{
                          bgcolor: a.priority === 'high' ? colors.dangerBg :
                            a.priority === 'medium' ? colors.warningBg : colors.successBg,
                          color: colors.charcoal,
                          fontSize: '0.72rem',
                          height: 26,
                          maxWidth: 350,
                          fontFamily: '"DM Sans", sans-serif',
                          fontWeight: 500,
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              </GlassCard>
            </ScrollReveal>
          )}

          {/* ===== ROW 4: 4 ENRICHED EXPANDABLE KPI CARDS (accordion, full-width on expand) ===== */}
          <Grid container spacing={2} sx={{ mb: 5 }}>
            {[
              {
                key: 'mentions',
                component: (
                  <ExpandableKPI
                    value={totalMentions || 0}
                    label="MENTIONS"
                    icon={<CampaignIcon sx={{ fontSize: 20 }} />}
                    info="Toutes les mentions de votre marque captees sur le web, reseaux sociaux, presse et RSS sur la periode selectionnee. Cliquez pour filtrer par plateforme ou sentiment."
                    summary={`${totalMentions || 0} mentions sur ${platformSources.length} plateforme${platformSources.length > 1 ? 's' : ''}`}
                    delta={todayKPIs?.trends?.mentions != null ? `${todayKPIs.trends.mentions >= 0 ? '+' : ''}${todayKPIs.trends.mentions.toFixed(1)}% vs moy. mensuelle` : undefined}
                    deltaType={todayKPIs?.trends?.mentions != null ? (todayKPIs.trends.mentions >= 0 ? 'positive' : 'negative') : undefined}
                    isExpanded={expandedKPI === 'mentions'}
                    onToggle={() => setExpandedKPI(prev => prev === 'mentions' ? null : 'mentions')}
                    expandedContent={
                      <Box onClick={(e) => e.stopPropagation()}>
                        {/* Filters */}
                        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                          <TextField
                            size="small"
                            placeholder="Rechercher..."
                            value={mentionSearch}
                            onChange={(e) => setMentionSearch(e.target.value)}
                            sx={{ minWidth: 150, flex: 1 }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon sx={{ fontSize: 16, color: colors.textLight }} />
                                </InputAdornment>
                              ),
                            }}
                          />
                          <FormControl size="small" sx={{ minWidth: 100 }}>
                            <Select value={mentionPlatform} onChange={(e) => setMentionPlatform(e.target.value)}>
                              <MenuItem value="all">Toutes</MenuItem>
                              <MenuItem value="twitter">Twitter</MenuItem>
                              <MenuItem value="facebook">Facebook</MenuItem>
                              <MenuItem value="instagram">Instagram</MenuItem>
                              <MenuItem value="linkedin">LinkedIn</MenuItem>
                              <MenuItem value="youtube">YouTube</MenuItem>
                              <MenuItem value="news">News</MenuItem>
                              <MenuItem value="rss">RSS</MenuItem>
                              <MenuItem value="web">Web</MenuItem>
                            </Select>
                          </FormControl>
                          <FormControl size="small" sx={{ minWidth: 100 }}>
                            <Select value={mentionSentiment} onChange={(e) => setMentionSentiment(e.target.value)}>
                              <MenuItem value="all">Sentiment</MenuItem>
                              <MenuItem value="positive">Positif</MenuItem>
                              <MenuItem value="neutral">Neutre</MenuItem>
                              <MenuItem value="negative">Negatif</MenuItem>
                            </Select>
                          </FormControl>
                          <FormControl size="small" sx={{ minWidth: 70 }}>
                            <Select value={periodDays} onChange={(e) => setPeriodDays(e.target.value)}>
                              <MenuItem value={7}>7j</MenuItem>
                              <MenuItem value={30}>30j</MenuItem>
                              <MenuItem value={90}>90j</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                        {/* Mentions list */}
                        {mentionsLoading ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {[1, 2, 3].map(i => <Skeleton key={i} variant="rounded" height={80} sx={{ borderRadius: '12px' }} />)}
                          </Box>
                        ) : mentions.length > 0 ? (
                          <>
                            <Grid container spacing={1.5}>
                              {mentions.map((mention) => (
                                <Grid item xs={12} key={mention.id || mention.external_id}>
                                  <MentionCard mention={mention} brandKeywords={brands?.find(b => b.id === selectedBrand)?.keywords || []} compact />
                                </Grid>
                              ))}
                            </Grid>
                            {mentions.length >= mentionsLimit && (
                              <Box sx={{ textAlign: 'center', mt: 2 }}>
                                <Button
                                  variant="text"
                                  onClick={() => setMentionsLimit(prev => prev + 10)}
                                  sx={{ color: colors.primary, textTransform: 'none', fontFamily: '"DM Sans", sans-serif', fontSize: '0.78rem' }}
                                >
                                  Voir plus
                                </Button>
                              </Box>
                            )}
                          </>
                        ) : (
                          <Typography variant="body2" sx={{ textAlign: 'center', py: 2, color: colors.textLight, fontFamily: '"DM Sans", sans-serif', fontSize: '0.8rem' }}>
                            Aucune mention trouvee.
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                ),
              },
              {
                key: 'sentiment',
                component: (
                  <ExpandableKPI
                    value={nssValue !== null ? `${nssValue.toFixed(0)}%` : '--'}
                    label="SENTIMENT NET"
                    icon={<HandshakeIcon sx={{ fontSize: 20 }} />}
                    info="Le Sentiment Net mesure l'equilibre entre mentions positives et negatives. 100% = que du positif, 0% = que du negatif, 50% = equilibre. Formule : (positifs - negatifs) / total × 100."
                    summary={(() => {
                      const pos = sentimentBreakdown.find(s => s.name === 'Positif')?.value || 0;
                      const neg = sentimentBreakdown.find(s => s.name === 'Negatif')?.value || 0;
                      const neu = sentimentBreakdown.find(s => s.name === 'Neutre')?.value || 0;
                      if (pos + neg + neu === 0) return 'Aucune donnee';
                      return `${pos} positive${pos > 1 ? 's' : ''} · ${neg} negative${neg > 1 ? 's' : ''} · ${neu} neutre${neu > 1 ? 's' : ''}`;
                    })()}
                    delta={todayKPIs?.trends?.nss != null ? `${todayKPIs.trends.nss >= 0 ? '+' : ''}${todayKPIs.trends.nss.toFixed(1)}% vs moy. mensuelle` : undefined}
                    deltaType={todayKPIs?.trends?.nss != null ? (todayKPIs.trends.nss >= 0 ? 'positive' : 'negative') : 'neutral'}
                    isExpanded={expandedKPI === 'sentiment'}
                    onToggle={() => setExpandedKPI(prev => prev === 'sentiment' ? null : 'sentiment')}
                    expandedContent={
                      <Box>
                        {hasSources || sentimentBreakdown.length > 0 ? (
                          <SegmentWheel
                            platformData={platformSources}
                            sentimentData={sentimentBreakdown}
                            totalMentions={totalMentions}
                          />
                        ) : (
                          <Typography variant="body2" sx={{ textAlign: 'center', py: 2, color: colors.textLight, fontFamily: '"DM Sans", sans-serif', fontSize: '0.8rem' }}>
                            Aucune donnee de segment.
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                ),
              },
              {
                key: 'trends',
                component: (
                  <ExpandableKPI
                    value={trendsCount}
                    label="TENDANCES"
                    icon={<CampaignIcon sx={{ fontSize: 20 }} />}
                    info="Sujets et themes emergents detectes par l'IA dans vos mentions. Identifie les narratifs dominants et les signaux faibles a surveiller."
                    summary={`${trendsCount} sujets detectes par l'IA`}
                    delta={trendsDelta?.label}
                    deltaType={trendsDelta ? (trendsDelta.diff > 0 ? 'positive' : trendsDelta.diff < 0 ? 'negative' : 'neutral') : undefined}
                    isExpanded={expandedKPI === 'trends'}
                    onToggle={() => setExpandedKPI(prev => prev === 'trends' ? null : 'trends')}
                    expandedContent={
                      <Box>
                        {hasKeywords && (
                          <Box sx={{ mb: 2 }}>
                            <Typography sx={{ fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: colors.textMed, fontFamily: '"DM Sans", sans-serif', fontWeight: 600, mb: 1 }}>
                              Mots-cles
                            </Typography>
                            <TrendingKeywordsCloud keywords={cloudKeywords} />
                          </Box>
                        )}
                        {hasTrends && (
                          <Box>
                            <Grid container spacing={1.5}>
                              {trends.slice(0, 4).map((trend) => (
                                <Grid item xs={12} key={trend.id}>
                                  <TrendPredictionCard prediction={trend} />
                                </Grid>
                              ))}
                            </Grid>
                          </Box>
                        )}
                        {!hasTrends && !hasKeywords && (
                          <Typography variant="body2" sx={{ textAlign: 'center', py: 2, color: colors.textLight, fontFamily: '"DM Sans", sans-serif', fontSize: '0.8rem' }}>
                            Aucune tendance detectee.
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                ),
              },
              {
                key: 'serp',
                component: (
                  <ExpandableKPI
                    value={scanningSerp ? '...' : hasSerp ? `${serpStats.score}%` : '--'}
                    label="SCORE SERP"
                    icon={<GroupsIcon sx={{ fontSize: 20 }} />}
                    info="Votre image sur Google : pourcentage de resultats positifs parmi les 10 premiers resultats de recherche. Un score eleve signifie que Google affiche une image favorable de votre marque."
                    summary={hasSerp ? `${serpStats.positive} positif${serpStats.positive > 1 ? 's' : ''} · ${serpStats.neutral} neutre${serpStats.neutral > 1 ? 's' : ''} · ${serpStats.negative} negatif${serpStats.negative > 1 ? 's' : ''} sur le top 10 Google` : 'Cliquez Actualiser'}
                    delta={serpDelta?.label}
                    deltaType={serpDelta ? (serpDelta.diff > 0 ? 'positive' : serpDelta.diff < 0 ? 'negative' : 'neutral') : undefined}
                    isExpanded={expandedKPI === 'serp'}
                    onToggle={() => setExpandedKPI(prev => prev === 'serp' ? null : 'serp')}
                    expandedContent={
                      <Box>
                        {hasSerp && (
                          <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', gap: 1.5, mb: 1 }}>
                              <Typography variant="caption" sx={{ color: colors.success, fontWeight: 600, fontFamily: '"DM Sans", sans-serif' }}>{serpStats.positive}+</Typography>
                              <Typography variant="caption" sx={{ color: colors.textLight, fontFamily: '"DM Sans", sans-serif' }}>{serpStats.neutral}~</Typography>
                              <Typography variant="caption" sx={{ color: colors.danger, fontWeight: 600, fontFamily: '"DM Sans", sans-serif' }}>{serpStats.negative}-</Typography>
                            </Box>
                            <Typography variant="body2" sx={{ color: colors.textMed, fontSize: '0.78rem', fontFamily: '"DM Sans", sans-serif', mb: 2 }}>
                              {serpStats.score >= 60 ? 'Bonne maitrise du top 10 Google' : serpStats.score >= 30 ? 'Resultats a consolider' : 'Resultats defavorables'}
                            </Typography>
                          </Box>
                        )}
                        {hasSources && (
                          <Box sx={{ mb: 2 }}>
                            <Typography sx={{ fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: colors.textMed, fontFamily: '"DM Sans", sans-serif', fontWeight: 600, mb: 1 }}>
                              Share of Voice
                            </Typography>
                            <ShareOfVoiceChart sources={platformSources} />
                          </Box>
                        )}
                        {serpData?.results?.length > 0 && (
                          <Box>
                            <Typography sx={{ fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: colors.textMed, fontFamily: '"DM Sans", sans-serif', fontWeight: 600, mb: 1 }}>
                              Top 10 Google
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {serpData.results
                                .filter(r => r.position <= 10)
                                .sort((a, b) => a.position - b.position)
                                .map((r) => (
                                  <Box
                                    key={r.id || r.position}
                                    component="a"
                                    href={r.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    sx={{
                                      display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.75, px: 1,
                                      borderRadius: '8px', textDecoration: 'none',
                                      '&:hover': { bgcolor: colors.emeraldGlass },
                                      transition: 'background-color 0.15s',
                                    }}
                                  >
                                    <Typography sx={{
                                      fontSize: '0.7rem', fontFamily: '"DM Sans", sans-serif', fontWeight: 600,
                                      fontVariantNumeric: 'tabular-nums',
                                      color: r.sentiment === 'positive' ? colors.success : r.sentiment === 'negative' ? colors.danger : colors.textLight,
                                      minWidth: 18, textAlign: 'right', mt: '1px',
                                    }}>
                                      {r.position}
                                    </Typography>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Typography sx={{
                                        fontSize: '0.78rem', fontFamily: '"DM Sans", sans-serif', fontWeight: 500,
                                        color: colors.text, lineHeight: 1.3,
                                        overflow: 'hidden', textOverflow: 'ellipsis',
                                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                                      }}>
                                        {r.title}
                                      </Typography>
                                      <Typography sx={{
                                        fontSize: '0.65rem', fontFamily: '"DM Sans", sans-serif',
                                        color: colors.textLight, mt: 0.25,
                                      }}>
                                        {r.domain}
                                      </Typography>
                                    </Box>
                                    <Box sx={{
                                      width: 6, height: 6, borderRadius: '50%', mt: '6px', flexShrink: 0,
                                      bgcolor: r.sentiment === 'positive' ? colors.success : r.sentiment === 'negative' ? colors.danger : colors.textLight,
                                    }} />
                                  </Box>
                                ))}
                            </Box>
                          </Box>
                        )}
                        {!hasSerp && !hasSources && (
                          <Typography variant="body2" sx={{ textAlign: 'center', py: 2, color: colors.textLight, fontFamily: '"DM Sans", sans-serif', fontSize: '0.8rem' }}>
                            Cliquez Actualiser pour scanner Google.
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                ),
              },
            ].map((kpi) => (
              <Grid
                item
                key={kpi.key}
                xs={12}
                sm={expandedKPI === kpi.key ? 12 : 6}
                md={expandedKPI === kpi.key ? 12 : 3}
              >
                {kpi.component}
              </Grid>
            ))}
          </Grid>

          {/* Reliability indicator */}
          {totalMentions > 0 && (
            <Typography
              variant="caption"
              sx={{
                fontFamily: "'DM Sans', sans-serif",
                color: colors.textLight,
                textAlign: 'center',
                display: 'block',
                mt: 1,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              Base sur {totalMentions} mention{totalMentions > 1 ? 's' : ''} analysee{totalMentions > 1 ? 's' : ''}
            </Typography>
          )}

          {/* Subtle hint when no mentions */}
          {totalMentions === 0 && !loading && (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "'DM Sans', sans-serif",
                  color: colors.textLight,
                  textWrap: 'pretty',
                }}
              >
                Aucune mention collectee pour le moment. Cliquez sur "Actualiser" pour lancer une collecte.
              </Typography>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

export default RadarPage;
